import * as React from 'react';
import { values } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Input, Label } from 'reactstrap';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import {
  CompetitionPledge,
  CompetitionPledgeCategories,
  CompetitionPledges
} from '../../userApi';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization } from '../../utils/wrappers';
import { makePledgePath } from '../../utils/routerHelper';
import {
  ELECTRICITY_DOLLARS_PER_KWH,
  GAS_DOLLARS_PER_THERM,
  GASOLINE_DOLLARS_PER_GALLON,
  WATER_AND_SEWER_DOLLARS_PER_GALLON,
  estimateActionSavings
} from '../../utils/actionSavings';
import Footer from '../../shared/Footer';

interface Props {
  pledges: CompetitionPledges;
  categories: CompetitionPledgeCategories;
}

interface ActionSavingsState {
  categoryID: number;
  search: string;
  costsOnly: boolean;
}

const formatOptional = (
  value: number,
  formatNumber: (value: number, decimals?: number) => string,
  decimals: number = 1
) => value ? formatNumber(value, decimals) : '—';

class ActionSavingsPage extends React.Component<LProps<Props>, ActionSavingsState> {
  public state: ActionSavingsState = {
    categoryID: 0,
    search: '',
    costsOnly: false
  };

  public componentDidMount() {
    const section = window.location.hash.replace('#', '');
    const element = section && document.getElementById(section);
    if (element) {
      element.scrollIntoView();
    }
  }

  public render() {
    const { t, formatNumber, pledges, categories } = this.props;
    const { categoryID, search, costsOnly } = this.state;
    const normalizedSearch = search.trim().toLowerCase();
    const categoryList = values(categories).sort((a, b) => a.name.localeCompare(b.name));
    const actionList = values(pledges)
      .filter((pledge) => pledge.active !== false && Number(pledge.savingsCO2Lbs || 0) > 0)
      .map((pledge) => ({ pledge, estimate: estimateActionSavings(pledge) }))
      .filter(({ pledge, estimate }) => {
        if (categoryID && pledge.competitionPledgeCategoryID !== categoryID) return false;
        if (costsOnly && !estimate.dollarsSaved) return false;
        return !normalizedSearch || pledge.shortTitle.toLowerCase().indexOf(normalizedSearch) >= 0;
      })
      .sort((a, b) => a.pledge.shortTitle.localeCompare(b.pledge.shortTitle));

    return (
      <div className="cc__page__column_content">
        <div style={{ width: '100%' }}>
          <div className="cc__white_box">
            <h2>{t('Action savings and costs')}</h2>
            <p className="cc__lighttext">
              {t('Estimated physical-unit and San Francisco dollar savings per credited action completion.')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <Label for="action-savings-search">{t('Search actions')}</Label>
                <Input
                  id="action-savings-search"
                  value={search}
                  onChange={(event) => this.setState({ search: event.target.value })}
                />
              </div>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <Label for="action-savings-category">{t('Category')}</Label>
                <Input
                  id="action-savings-category"
                  type="select"
                  value={categoryID}
                  onChange={(event) => this.setState({ categoryID: Number(event.target.value) })}
                >
                  <option value={0} aria-selected={categoryID === 0}>{t('All categories')}</option>
                  {categoryList.map((category) =>
                    <option
                      key={category.id}
                      value={category.id}
                      aria-selected={categoryID === category.id}
                    >{category.name}</option>
                  )}
                </Input>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={costsOnly}
                    onChange={(event) => this.setState({ costsOnly: event.target.checked })}
                  />{' '}
                  {t('Only actions with local cost savings')}
                </Label>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped table-sm">
                <thead>
                  <tr>
                    <th>{t('Action')}</th>
                    <th>{t('Category')}</th>
                    <th className="text-right">{t('CO2 (lb)')}</th>
                    <th className="text-right">{t('kWh')}</th>
                    <th className="text-right">{t('Therms')}</th>
                    <th className="text-right">{t('Gasoline (gal)')}</th>
                    <th className="text-right">{t('Water (gal)')}</th>
                    <th className="text-right">{t('SF dollars saved')}</th>
                    <th>{t('Estimate basis')}</th>
                  </tr>
                </thead>
                <tbody>
                  {actionList.map(({ pledge, estimate }) =>
                    <tr key={pledge.id}>
                      <td><Link to={makePledgePath(pledge, t)}>{pledge.shortTitle}</Link></td>
                      <td>
                        {categories[pledge.competitionPledgeCategoryID]
                          ? categories[pledge.competitionPledgeCategoryID].name
                          : '—'
                        }
                      </td>
                      <td className="text-right">{formatNumber(pledge.savingsCO2Lbs, 0)}</td>
                      <td className="text-right">{formatOptional(estimate.electricityKwh, formatNumber)}</td>
                      <td className="text-right">{formatOptional(estimate.naturalGasTherms, formatNumber)}</td>
                      <td className="text-right">{formatOptional(estimate.gasolineGallons, formatNumber)}</td>
                      <td className="text-right">{formatOptional(estimate.waterGallons, formatNumber, 0)}</td>
                      <td className="text-right">
                        {estimate.dollarsSaved ? `$${formatNumber(estimate.dollarsSaved, 2)}` : '—'}
                      </td>
                      <td>{estimate.basis}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div id="methodology" className="cc__white_box" style={{ marginTop: '20px' }}>
            <h2>{t('Savings methodology')}</h2>
            <p>
              {t('Catalog CO2, electricity, and water values are shown per credited completion. Existing catalog physical-unit values take priority over inferred values.')}
            </p>
            <h5>{t('September 2026 San Francisco planning rates')}</h5>
            <ul>
              <li>PG&amp;E E-1 Tier 1 electricity: ${ELECTRICITY_DOLLARS_PER_KWH}/kWh</li>
              <li>PG&amp;E G-1 baseline natural gas: ${GAS_DOLLARS_PER_THERM}/therm</li>
              <li>EIA San Francisco regular gasoline: ${GASOLINE_DOLLARS_PER_GALLON}/gallon</li>
              <li>SFPUC variable water and sewer: ${WATER_AND_SEWER_DOLLARS_PER_GALLON}/gallon</li>
            </ul>
            <h5>{t('Conversion rules')}</h5>
            <ul>
              <li>{t('Transportation CO2 is converted using the EPA factor of 19.592 lb CO2 per gallon of gasoline.')}</li>
              <li>{t('Natural gas uses the EPA factor of 11.684 lb CO2 per therm.')}</li>
              <li>{t('Inferred electricity uses the EPA CAMX regional-grid factor of 0.4285 lb CO2 per kWh.')}</li>
              <li>{t('The legacy water field is treated as gallons because the 2,700 showerhead value matches EPA WaterSense.')}</li>
              <li>{t('Water and sewer savings apply only to actions that plausibly reduce the participant’s metered SFPUC use.')}</li>
            </ul>
            <p className="cc__lighttext">
              {t('These are screening estimates, not guaranteed bill reductions. Lifecycle food, product, waste, finance, community, and remote water impacts are not assigned local utility-dollar savings without defensible billing assumptions.')}
            </p>
            <h5>{t('Sources')}</h5>
            <ul>
              <li><a href="https://www.pge.com/tariffs/assets/pdf/tariffbook/ELEC_SCHEDS_E-1.pdf" target="_blank" rel="noopener noreferrer">PG&amp;E E-1 tariff</a></li>
              <li><a href="https://www.pge.com/tariffs/assets/pdf/tariffbook/GAS_SCHEDS_G-1.pdf" target="_blank" rel="noopener noreferrer">PG&amp;E G-1 tariff</a></li>
              <li><a href="https://www.sfpuc.gov/sites/default/files/accounts-and-services/Rates_Schedule_Water_Sewer_2026-7.pdf" target="_blank" rel="noopener noreferrer">SFPUC FY 2026–27 rates</a></li>
              <li><a href="https://www.eia.gov/dnav/pet/PET_PRI_GND_DCUS_Y05SF_W.htm" target="_blank" rel="noopener noreferrer">EIA San Francisco gasoline prices</a></li>
              <li><a href="https://www.epa.gov/energy/greenhouse-gas-equivalencies-calculator-calculations-and-references" target="_blank" rel="noopener noreferrer">EPA greenhouse-gas equivalencies</a></li>
              <li><a href="https://www.epa.gov/watersense/showerheads" target="_blank" rel="noopener noreferrer">EPA WaterSense showerheads</a></li>
            </ul>
          </div>
          <Footer />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  pledges: state.competitionPledges || {},
  categories: state.competitionPledgeCategories || {}
});

export default asPage({
  isGrey: true,
  requiresCompetitionStart: true,
  requiresCompetitionVerification: true
})(compose(
  withLocalization('pledge'),
  connect(mapStateToProps)
)(ActionSavingsPage));
